/**
 * Home section registry.
 *
 * The home page renders a fixed set of sections. The admin controls
 * which are visible and in what order via `/api/home-sections`; this
 * registry maps each stable key to its component + the height to
 * reserve while it lazy-mounts (avoids CLS).
 *
 * `Hero` is intentionally NOT here — it's always rendered first and
 * can't be hidden (it owns the LCP + page identity).
 */

import type { ComponentType } from 'react'
import { HeroMarquee } from './components/hero-marquee'
import { Specialties } from './components/specialties'
import { Projects } from './components/projects'
import { FeaturedCourses } from './components/featured-courses'
import { ResourcesShowcase } from './components/resources-showcase'
import { Testimonials } from './components/testimonials'

export type HomeSectionKey =
  | 'marquee'
  | 'specialties'
  | 'projects'
  | 'courses'
  | 'resources'
  | 'testimonials'

interface HomeSectionDef {
  key: HomeSectionKey
  Component: ComponentType
  /** Reserved height while deferred-mounting (px). 0 = mount eagerly. */
  minHeight: number
}

/** Default order — matches the seed in migration 0076 + steering §6.7.1. */
export const HOME_SECTIONS: HomeSectionDef[] = [
  { key: 'marquee', Component: HeroMarquee, minHeight: 0 },
  { key: 'specialties', Component: Specialties, minHeight: 620 },
  { key: 'projects', Component: Projects, minHeight: 560 },
  { key: 'courses', Component: FeaturedCourses, minHeight: 620 },
  { key: 'resources', Component: ResourcesShowcase, minHeight: 680 },
  { key: 'testimonials', Component: Testimonials, minHeight: 520 },
]

export const HOME_SECTION_MAP: Record<HomeSectionKey, HomeSectionDef> =
  HOME_SECTIONS.reduce(
    (acc, s) => {
      acc[s.key] = s
      return acc
    },
    {} as Record<HomeSectionKey, HomeSectionDef>,
  )
